import { useEffect } from "react";
import type { JSX, ReactNode } from "react";
import { AppRuntimeProvider } from "./providers/AppRuntimeProvider";
import type { ModuleDefinition } from "./runtime/contracts";
import { RouterProvider } from "./providers/RouterProvider";
import { WebSocketProvider } from "./providers/WebSocketProvider";
import { AnalyticsAppEvents, AnalyticsProvider } from "./analytics/provider";
import { useWebSocket } from "./hooks/useWebSocket";
import { AppLayout } from "./layout/AppLayout";
import { APP_MODULES } from "./modules";
import { useUiStore } from "./store/uiStore";
import { LoginGate } from "./pages/LoginGate";
import { useAuthStore } from "./store/authStore";
import { useWorkspaceStore } from "./store/workspaceStore";
import { DemoGate } from "./onboarding/DemoGate";
import { FullGate } from "./onboarding/FullGate";
import { OnboardingProvider } from "./onboarding/OnboardingProvider";

function AppContent(): JSX.Element {
  const { backendReady, status } = useWebSocket();
  const setBackendReady = useUiStore((s) => s.setBackendReady);
  const theme = useUiStore((s) => s.theme);

  useEffect(() => {
    setBackendReady(backendReady);
  }, [backendReady, setBackendReady]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function applyTheme(mode: "dark" | "light" | "system") {
      let resolved: "dark" | "light";
      if (mode === "system") {
        resolved = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      } else {
        resolved = mode;
      }
      document.documentElement.dataset.theme = resolved;
    }

    applyTheme(theme);

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handler = () => { applyTheme("system"); };
    if (theme === "system") {
      mq.addEventListener("change", handler);
    }
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    return () => { mq.removeEventListener("change", handler); };
  }, [theme]);

  return (
    <OnboardedApp moduleDefinitions={APP_MODULES}>
      <AnalyticsProvider>
        <RouterProvider>
          <AnalyticsAppEvents />
          <AppLayout
            fallback={
              <div className="flex items-center justify-center h-full text-content-tertiary text-base">
                {status === "Initializing..." ? status : "Module not found"}
              </div>
            }
          />
        </RouterProvider>
      </AnalyticsProvider>
    </OnboardedApp>
  );
}

export function App(): JSX.Element {
  const ready = useWorkspaceStore((state) => state.ready);
  const initialize = useWorkspaceStore((state) => state.initialize);
  const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspaceId);
  const authenticatedUserId = useAuthStore((state) => state.user?.id ?? null);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-surface-primary text-content-secondary">
        Loading workspace...
      </div>
    );
  }

  // @tested-by: tst_fe_app_workspace_001, tst_fe_app_workspace_002
  // The runtime owns user-scoped query caches and the authenticated socket.
  // Reusing it across Google accounts can display the previous account's
  // cached empty state even after the new tenant has finished seeding.
  const workspaceBoundaryKey =
    `${selectedWorkspaceId ?? "no-workspace"}:${authenticatedUserId ?? "anonymous"}`;

  return (
    <WebSocketProvider key={workspaceBoundaryKey}>
      <LoginGate>
        <AppContent />
      </LoginGate>
    </WebSocketProvider>
  );
}

/**
 * The whole onboarding sandwich, exported so a test renders THIS rather than a
 * hand-built copy of it — including where the runtime sits between the gates,
 * which is the part that matters.
 *
 * `DemoGate` sits ABOVE `AppRuntimeProvider` because the provider runs every
 * module's `setup()`, and the standard `setup()` prefetches that module's list
 * — a gate placed under it could not keep a half-seeded workspace from being
 * queried. That half is pinned: `tst_fe_onboarding_001` renders this component
 * and fails if the gate moves under the runtime.
 *
 * `FullGate` sits UNDER the provider because the wizard reuses `useRpcQuery`,
 * `SearchIndexingPanel`, `useAgentEngine` and `SourceConnect`, none of which
 * exist without it. That half is NOT pinned by a test and does not need to be:
 * `useRpcQuery` calls `useAppRuntime()` unconditionally, so hoisting `FullGate`
 * above the provider throws "useAppRuntime must be used inside
 * AppRuntimeProvider" on first paint, for every user. The compiler cannot catch
 * it; the app refusing to render at all does.
 *
 * @tested-by: tst_fe_onboarding_001
 */
export function OnboardedApp({
  moduleDefinitions,
  children,
}: {
  readonly moduleDefinitions: readonly ModuleDefinition[];
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <OnboardingProvider>
      <DemoGate>
        <AppRuntimeProvider moduleDefinitions={moduleDefinitions}>
          <FullGate>{children}</FullGate>
        </AppRuntimeProvider>
      </DemoGate>
    </OnboardingProvider>
  );
}
